import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Card,
    TextField,
    Autocomplete,
    Button,
    MenuItem,
    Box,
    InputAdornment,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Modal,
    Checkbox,
    IconButton,
    CircularProgress,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import certificateImage from '../../../assets/certificate.png';
import { ToastContainer, toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import statusImg from '../../../assets/status.png';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PeopleIcon from '@mui/icons-material/People';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { makeApiRequest } from '../../../utils/api';

const MARKS_COMPONENT_MAP = {
    'DOMAIN TECHNICAL CONCEPT': ['written', 'viva'],
    'DRAFTING': ['written', 'viva', 'project_presentation'],
    'MODELING': ['written', 'viva', 'project_presentation'],
    'ATTENDANCE': ['attendance'],
    'PROJECT (DRAFTING + MODELING)': ['project_understanding', 'explanation_technical', 'tools_skill'],
};

const MARKS_MAX_MAP = {
    'domain technical concept': { written: 5, viva: 5 },
    'drafting': { written: 3, viva: 3, project_presentation: 4 },
    'modeling': { written: 3, viva: 3, project_presentation: 4 },
    'attendance': { attendance: 10 },
    'project (drafting + modeling)': { project_understanding: 3, explanation_technical: 3, tools_skill: 4 }
};

const getMarkMax = (field, assessmentName) => {
    const key = (assessmentName || '').toLowerCase().trim();
    return MARKS_MAX_MAP[key]?.[field] ?? 10;
};

// Mirrors the grade thresholds used at certificate render time in secondPage.blade.php
const GRADE_THRESHOLDS = [
    { min: 90, grade: 'A+', label: 'Outstanding' },
    { min: 80, grade: 'A', label: 'Excellent' },
    { min: 70, grade: 'B+', label: 'Proficient' },
    { min: 60, grade: 'B', label: 'Competent' },
    { min: 50, grade: 'C', label: 'Developing' },
    { min: 0, grade: 'D', label: 'Requires Improvement' },
];

const getGradeForMark = (mark) => GRADE_THRESHOLDS.find(t => mark >= t.min) || GRADE_THRESHOLDS[GRADE_THRESHOLDS.length - 1];

const toSentenceCase = (str) => str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const toInputDate = (dmy) => {
    if (!dmy || !dmy.includes('-')) return '';
    const [d, m, y] = dmy.split('-');
    return `${y}-${m}-${d}`;
};

const fromInputDate = (ymd) => {
    if (!ymd || !ymd.includes('-')) return '';
    const [y, m, d] = ymd.split('-');
    return `${d}-${m}-${y}`;
};

function GenerateCertificatePage() {
    const control = localStorage.getItem('control');
    const userLocation = localStorage.getItem('userLocation');
    const [formData, setFormData] = useState({
        location: (control == 4) ? userLocation : '',
        batch: '',
        name: '',
    });
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    const navigate = useNavigate();
    const [searchResults, setSearchResults] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [activeTab, setActiveTab] = useState('ready');
    const [batches, setBatches] = useState([]);
    const [searchTriggered, setSearchTriggered] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [userid, setUserid] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [eligible, setEligible] = useState({ valid: [], invalid: [] });

    // Sequential preview & edit flow (shown when Generate Certificate is confirmed)
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewQueue, setPreviewQueue] = useState([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewMarksLoading, setPreviewMarksLoading] = useState(false);
    const [previewCertificateId, setPreviewCertificateId] = useState(null);
    const [previewBaseline, setPreviewBaseline] = useState(null);
    const [previewForm, setPreviewForm] = useState({ student_name: '', course: '', date: '', start_date: '', end_date: '' });
    const [previewAssessments, setPreviewAssessments] = useState([]);
    const [previewEditedMarks, setPreviewEditedMarks] = useState({});
    const [previewInitialMarks, setPreviewInitialMarks] = useState({});
    const [previewSubmitting, setPreviewSubmitting] = useState(false);
    const [previewCurrentImage, setPreviewCurrentImage] = useState(null);
    const [previewImagePreviewUrl, setPreviewImagePreviewUrl] = useState(null);
    const [previewStagedImagePath, setPreviewStagedImagePath] = useState(null);
    const [previewImageUploading, setPreviewImageUploading] = useState(false);
    const [previewEditMode, setPreviewEditMode] = useState(false);
    const [previewStudentMode, setPreviewStudentMode] = useState(null);
    const [immediateIds, setImmediateIds] = useState([]);
    const [pendingIds, setPendingIds] = useState([]);
    const isReadyStatus = (status) => status === 'ready';
    const isPendingStatus = (status) => ['pending', 'notready'].includes(status);
    const showIllustration = !searchTriggered || (searchTriggered && searchResults.length === 0);
    const filteredResults = searchResults.filter(row => {
        return activeTab === 'ready' ? isReadyStatus(row.status) : isPendingStatus(row.status);
    });
    const paginatedRows = filteredResults.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const eligibleUserIds = paginatedRows
        .filter(row => row.eligible_status?.status === "eligible" && isReadyStatus(row.status))
        .map(row => row.userid);

    const isAllSelected = eligibleUserIds.length > 0 && eligibleUserIds.every(id => selectedUserIds.includes(id));
    const isIndeterminate = eligibleUserIds.some(id => selectedUserIds.includes(id)) && !isAllSelected;

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedUserIds(prev => prev.filter(id => !eligibleUserIds.includes(id)));
        } else {
            const updated = Array.from(new Set([...selectedUserIds, ...eligibleUserIds]));
            setSelectedUserIds(updated);
        }
    };

    const handleSearch = async () => {
        try {
            if (!formData.batch && !formData.location && !formData.name) {
                throw new Error('Please select any Fields');
            }
            const results = await makeApiRequest('post', '/api/students/filter/certificate', {
                token: localStorage.getItem('authToken'),
                data: {
                    batch_id: formData.batch,
                    control: 3,
                    location: formData.location,
                    search: formData.name

                }
            });

            if (results.status === "success" && results.data.list.length != 0) {
                setSearchResults(results.data.list);
                setSearchTriggered(true);
            } else {
                setSearchResults([]);
            }
            if (results.data.list.length === 0) {
                toast.info("No data found");
            }
        } catch (err) {
            console.error(err);
            toast.error(err.message || 'Something went wrong');
        }
    };

    const fetchBatchData = async () => {
        try {
            const response = await makeApiRequest('post', '/api/listBatches/active', {
                token: localStorage.getItem('authToken'),
            });
            if (response?.status === 'success' && response.data) {
                let data = response.data;
                if (control == 4 && userLocation) {
                    data = data.filter(batch => Number(batch.location) === Number(userLocation));
                }
                setBatches(data);
            } else {
                setBatches([]);
            }
        } catch (err) {
            console.error("Error fetching batches:", err);
            setBatches([]);
        }
    };
    useEffect(() => {
        fetchBatchData();
    }, []);

    const filteredBatches = formData.location
        ? batches.filter((batch) => Number(batch.location) === Number(formData.location))
        : batches;

    useEffect(() => {
        if (formData.batch && !filteredBatches.some((batch) => batch.id === formData.batch)) {
            setFormData((prev) => ({ ...prev, batch: '' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.location, batches]);
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleChangeTab = (event, newValue) => {
        setActiveTab(newValue);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
    };
    const handleGenerateClick = () => {
        setOpenModal(true);
    };
    useEffect(() => {
        if (paginatedRows && paginatedRows.length > 0) {
            const ready = paginatedRows
                .filter(row => isReadyStatus(row.status))
                .map(row => row.userid);

            const hasChanged = ready.length !== userid.length || !ready.every((id, index) => id === userid[index]);

            if (hasChanged) {
                setUserid(ready);
            }
        }
    }, [paginatedRows]);

    useEffect(() => {
        if (activeTab === "ready") {
            const defaultSelected = paginatedRows
                .filter(row => row.eligible_status?.status === "eligible" && isReadyStatus(row.status))
                .map(row => row.userid);

            const hasChanged = defaultSelected.some(id => !selectedUserIds.includes(id)) ||
                selectedUserIds.some(id => !defaultSelected.includes(id));

            if (hasChanged) {
                setSelectedUserIds(defaultSelected);
            }
        }
    }, [paginatedRows, activeTab]);


    const handleClick = () => {
        if (selectedUserIds.length === 0) {
            toast.warn("Please select at least one student.");
            return;
        }

        setOpenModal(false);
        setImmediateIds([]);
        setPendingIds([]);
        setPreviewQueue(selectedUserIds);
        setPreviewIndex(0);
        setPreviewModalOpen(true);
        loadPreviewStudent(selectedUserIds[0]);
    };

    const loadPreviewStudent = async (studentUserId) => {
        setPreviewLoading(true);
        setPreviewAssessments([]);
        setPreviewEditedMarks({});
        setPreviewInitialMarks({});
        setPreviewCertificateId(null);
        setPreviewBaseline(null);
        setPreviewCurrentImage(null);
        setPreviewImagePreviewUrl(null);
        setPreviewStagedImagePath(null);
        setPreviewEditMode(false);
        setPreviewStudentMode(null);

        try {
            const response = await makeApiRequest('post', '/api/certificate/preview', {
                token: localStorage.getItem('authToken'),
                userid: studentUserId
            });

            if (response?.status === 'success' && response.data) {
                const data = response.data;
                setPreviewCertificateId(data.id);
                setPreviewBaseline(data);
                setPreviewCurrentImage(data.image || null);
                setPreviewStudentMode(data.mode ? data.mode.toLowerCase() : null);
                setPreviewForm({
                    student_name: data.student_name || '',
                    course: data.course || '',
                    date: toInputDate(data.date),
                    start_date: toInputDate(data.start_date),
                    end_date: toInputDate(data.end_date),
                });
            } else {
                toast.error(`Failed to load preview for ${studentUserId}`);
            }
        } catch (err) {
            console.error('Error loading certificate preview:', err);
            toast.error(`Failed to load preview for ${studentUserId}`);
        } finally {
            setPreviewLoading(false);
        }

        const row = searchResults.find(r => r.userid === studentUserId);
        if (!row?.id) return;

        setPreviewMarksLoading(true);
        try {
            const marksResponse = await makeApiRequest('post', '/api/getStudentMarks', {
                token: localStorage.getItem('authToken'),
                id: Number(row.id)
            });

            if (marksResponse?.data?.length > 0) {
                const sData = marksResponse.data[0];
                setPreviewAssessments(sData.assessments || []);

                const marksObj = {};
                (sData.assessments || []).forEach(item => {
                    const fields = MARKS_COMPONENT_MAP[item.generic_name] || [];
                    marksObj[item.assessment_id] = {};
                    fields.forEach(field => {
                        const val = item.marks[field];
                        marksObj[item.assessment_id][field] = val !== null && val !== undefined ? val : '';
                    });
                });
                setPreviewEditedMarks(JSON.parse(JSON.stringify(marksObj)));
                setPreviewInitialMarks(JSON.parse(JSON.stringify(marksObj)));
            }
        } catch (err) {
            console.error('Error loading marks for preview:', err);
        } finally {
            setPreviewMarksLoading(false);
        }
    };

    const finishPreviewFlow = async (finalImmediateIds, finalPendingIds) => {
        setPreviewModalOpen(false);
        setPreviewQueue([]);
        setPreviewIndex(0);

        if (finalPendingIds.length > 0) {
            toast.info(`${finalPendingIds.length} certificate edit request(s) submitted, pending admin approval.`);
        }

        if (finalImmediateIds.length > 0) {
            await generateCertificate(finalImmediateIds);
        }
    };

    const advanceQueue = async (nextImmediateIds, nextPendingIds) => {
        const nextIndex = previewIndex + 1;
        if (nextIndex < previewQueue.length) {
            setPreviewIndex(nextIndex);
            await loadPreviewStudent(previewQueue[nextIndex]);
        } else {
            await finishPreviewFlow(nextImmediateIds, nextPendingIds);
        }
    };

    const handlePreviewFieldChange = (field, value) => {
        setPreviewForm(prev => ({ ...prev, [field]: value }));
    };

    const calculatePreviewAssessmentTotal = (assessmentId) => {
        const marks = previewEditedMarks[assessmentId] || {};
        return Object.values(marks).reduce((sum, val) => sum + (val === '' || val === null || val === undefined ? 0 : Number(val)), 0);
    };

    // Mirrors AssessmentService::formatMarksOfStudent's overall_pass formula, so the
    // overall total/grade shown here matches what secondPage.blade.php prints on the PDF.
    const calculatePreviewOverallTotal = () => {
        if (previewAssessments.length === 0) return 0;
        const subjectTotals = previewAssessments.map(item => calculatePreviewAssessmentTotal(item.assessment_id));
        const avg = subjectTotals.reduce((sum, val) => sum + val, 0) / previewAssessments.length;
        return Math.round(avg * 100) / 100 * 10;
    };

    const handlePreviewMarkChange = (assessmentId, field, value) => {
        setPreviewEditedMarks(prev => ({
            ...prev,
            [assessmentId]: {
                ...prev[assessmentId],
                [field]: value === '' ? '' : Number(value)
            }
        }));
    };

    const handleUseAsIs = async () => {
        const nextImmediate = [...immediateIds, previewQueue[previewIndex]];
        setImmediateIds(nextImmediate);
        await advanceQueue(nextImmediate, pendingIds);
    };

    const handleCancelStudentEdit = () => {
        if (previewBaseline) {
            setPreviewForm({
                student_name: previewBaseline.student_name || '',
                course: previewBaseline.course || '',
                date: toInputDate(previewBaseline.date),
                start_date: toInputDate(previewBaseline.start_date),
                end_date: toInputDate(previewBaseline.end_date),
            });
        }
        setPreviewEditedMarks(JSON.parse(JSON.stringify(previewInitialMarks)));
        setPreviewImagePreviewUrl(null);
        setPreviewStagedImagePath(null);
        setPreviewEditMode(false);
    };

    const handleCancelPreview = () => {
        setPreviewModalOpen(false);
        setPreviewQueue([]);
        setPreviewIndex(0);
        setImmediateIds([]);
        setPendingIds([]);
        setPreviewCertificateId(null);
        setPreviewBaseline(null);
        setPreviewCurrentImage(null);
        setPreviewImagePreviewUrl(null);
        setPreviewStagedImagePath(null);
    };

    const handlePreviewImageSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            toast.warn('Only JPG, JPEG, and PNG files are allowed.');
            return;
        }

        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });

        setPreviewImagePreviewUrl(base64);
        setPreviewImageUploading(true);
        try {
            const response = await makeApiRequest('post', '/api/certificate/preview/image', {
                token: localStorage.getItem('authToken'),
                userid: previewQueue[previewIndex],
                image: base64
            });

            if (response?.status === 'success' && response.data?.staged_path) {
                setPreviewStagedImagePath(response.data.staged_path);
                toast.success('New photo staged — submit an edit request to apply it.');
            } else {
                toast.error(response?.message || 'Failed to upload photo.');
                setPreviewImagePreviewUrl(null);
            }
        } catch (err) {
            console.error('Error staging preview image:', err);
            toast.error(err?.response?.data?.message || 'Failed to upload photo.');
            setPreviewImagePreviewUrl(null);
        } finally {
            setPreviewImageUploading(false);
        }
    };

    const buildPreviewDiff = () => {
        const oldData = {};
        const newData = {};

        if (!previewBaseline) return { oldData, newData };

        const fieldMap = {
            student_name: previewBaseline.student_name || '',
            course: previewBaseline.course || '',
            date: previewBaseline.date || '',
            start_date: previewBaseline.start_date || '',
            end_date: previewBaseline.end_date || '',
        };
        const editedFieldMap = {
            student_name: previewForm.student_name,
            course: previewForm.course,
            date: fromInputDate(previewForm.date),
            start_date: fromInputDate(previewForm.start_date),
            end_date: fromInputDate(previewForm.end_date),
        };

        Object.keys(fieldMap).forEach(key => {
            if (fieldMap[key] !== editedFieldMap[key]) {
                oldData[key] = fieldMap[key];
                newData[key] = editedFieldMap[key];
            }
        });

        const marksOld = [];
        const marksNew = [];
        previewAssessments.forEach(item => {
            const aId = item.assessment_id;
            const fields = MARKS_COMPONENT_MAP[item.generic_name] || [];
            let hasChanged = false;
            const proposed = {};
            const current = {};

            fields.forEach(field => {
                const newVal = previewEditedMarks[aId]?.[field];
                const oldVal = previewInitialMarks[aId]?.[field];
                proposed[field] = newVal === '' ? null : newVal;
                current[field] = oldVal === '' ? null : oldVal;
                if (newVal !== oldVal) hasChanged = true;
            });

            if (hasChanged) {
                marksNew.push({ assessment_id: aId, assessment_name: item.generic_name, marks: proposed });
                marksOld.push({ assessment_id: aId, assessment_name: item.generic_name, marks: current });
            }
        });

        if (marksNew.length > 0) {
            oldData.marks = marksOld;
            newData.marks = marksNew;
        }

        if (previewStagedImagePath) {
            oldData.image = previewCurrentImage || null;
            newData.image = previewStagedImagePath;
        }

        return { oldData, newData };
    };

    const handleSubmitPreviewEditRequest = async () => {
        if (!previewBaseline || !previewCertificateId) return;
        setPreviewSubmitting(true);
        try {
            const { oldData, newData } = buildPreviewDiff();

            if (Object.keys(newData).length === 0) {
                toast.info('No changes detected — using the original data.');
                await handleUseAsIs();
                return;
            }

            const response = await makeApiRequest('post', '/api/edit-requests/save', {
                token: localStorage.getItem('authToken'),
                module: 'certificate',
                reference_id: previewCertificateId,
                old_data: oldData,
                new_data: newData
            });

            if (response?.status === 'success') {
                const nextPending = [...pendingIds, previewQueue[previewIndex]];
                setPendingIds(nextPending);
                await advanceQueue(immediateIds, nextPending);
            } else {
                toast.error(response?.message || 'Failed to submit edit request.');
            }
        } catch (err) {
            console.error('Error submitting certificate edit request:', err);
            toast.error(err?.response?.data?.message || 'Failed to submit edit request.');
        } finally {
            setPreviewSubmitting(false);
        }
    };

    const generateCertificate = async (readyUserIds) => {
        try {
            const response = await makeApiRequest('post', '/api/certificate/generate', {
                userids: readyUserIds,
                token: localStorage.getItem('authToken')
            });

            if (response.status === "success") {
                toast.success(response.message);
                setOpenModal(false);
                navigate('/generated-certificate', {
                    replace: true,
                    state: {
                        startDate: formattedDate,
                        endDate: formattedDate
                    }
                });
            } else if (response.data?.invalid?.length) {
                response.data.invalid.forEach((msg) => {
                    toast.warn(`${msg.userid} has ${msg.reason}`);
                });
            } else {
                toast.error('Failed to generate certificates');
            }
        } catch (err) {
            console.error(err);
            toast.error(err.message || 'Something went wrong while generating certificates');
        }
    };
    const handleErrorButton = () => {
        toast.error("Profile is Missing");
    }
    const handleCheckboxChange = (userid) => {
        setSelectedUserIds(prev => {
            if (prev.includes(userid)) {
                return prev.filter(id => id !== userid);
            } else {
                return [...prev, userid];
            }
        });
    };

    return (
        <Box
            sx={{
                backgroundColor: '#F1F4F9',
                minHeight: '100vh',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',

            }}
        >
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover={false}
                theme="light"
            />
            <Box>
                <Typography variant="h4" style={{ marginLeft: '30px', color: '#481E82', fontSize: '34px', fontFamily: ' sans-serif' }}>
                    Generate Certificate
                </Typography>
                <Card
                    sx={{
                        p: 3,
                        my: 3,
                        mx: 'auto',
                        display: 'flex',
                        gap: 3,
                        alignItems: 'center',
                        width: '90%',
                        maxWidth: '1350px',
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'center', md: 'flex-start' },
                    }}
                >
                    <TextField
                        size="small"
                        label="Location"
                        variant="outlined"
                        select
                        value={formData.location}
                        disabled={control == 4}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        sx={{ width: 330, fontFamily: 'sans-serif' }}
                        InputLabelProps={{ shrink: true }}
                    >
                        <MenuItem value="1">Chennai</MenuItem>
                        <MenuItem value="2">Trichy</MenuItem>
                    </TextField>
                    <Autocomplete
                        disablePortal
                        options={filteredBatches}
                        getOptionLabel={(option) => option.batch_name || ''}
                        value={filteredBatches.find((batch) => batch.id === formData.batch) || null}
                        onChange={(event, newValue) => {
                            setFormData({ ...formData, batch: newValue ? newValue.id : '' });
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disableClearable
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Batch"
                                placeholder="Select Batch"
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                        sx={{ width: 330, fontFamily: 'sans-serif' }}
                    />

                    <TextField
                        size="small"
                        label="Search"
                        placeholder="Student name or ID"
                        variant="outlined"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        sx={{ width: 330, fontFamily: 'sans-serif' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button variant="contained" color="primary" onClick={handleSearch} style={{ width: '200px', fontFamily: 'sans-serif', height: '40px' }}>
                        Search
                    </Button>
                </Card>
                {!showIllustration ? (
                    <>
                        <Card sx={{
                            marginTop: '30px',
                            marginLeft: '30px',
                            display: 'flex',
                            gap: '30px',
                            alignItems: 'center',
                            width: '100%',
                            maxWidth: '1406px',
                            flexWrap: 'wrap',
                        }}>
                            <Box >
                                <Tabs
                                    value={activeTab}
                                    onChange={handleChangeTab}
                                    aria-label="certificate-tabs"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        '& .MuiTabs-indicator': {
                                            bottom: '10px',
                                            width: '40%',
                                            transition: 'all 0.2s ease-in-out',
                                        },
                                        '& .MuiTab-root': {
                                            fontSize: '1rem',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: '8px',
                                            minWidth: 'auto',
                                            padding: '12px 16px',
                                            paddingLeft: '50px',
                                        },
                                        '& .MuiTab-iconWrapper': {
                                            marginBottom: 0,
                                            marginRight: '10px',
                                        },
                                    }}
                                >
                                    <Tab icon={<PeopleIcon />} label={`READY TO GENERATE`} value="ready" />
                                    <Tab icon={<PendingActionsIcon />} label={`PENDING WITH ASSESSMENTS`} value="pending" />
                                </Tabs>
                            </Box>
                        </Card>
                        <TableContainer component={Paper} sx={{
                            borderRadius: 0,
                            mx: 'auto',
                            width: '100%',
                            maxWidth: '1406px',
                            overflowX: 'auto',
                        }}>
                            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                <TableHead sx={{ backgroundColor: '#CAF0F8' }}>
                                    <TableRow>
                                        {activeTab === "ready" && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isAllSelected}
                                                    indeterminate={isIndeterminate}
                                                    onChange={handleSelectAll}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>S no</TableCell>
                                        <TableCell>Student Name</TableCell>
                                        <TableCell>Department</TableCell>
                                        <TableCell>Batch / Course</TableCell>
                                        {activeTab === "ready" && <TableCell>Status</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.map((row, index) => (
                                        <TableRow key={index}>
                                            {activeTab === "ready" && (
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedUserIds.includes(row.userid)}
                                                        onChange={() => handleCheckboxChange(row.userid)}
                                                        disabled={!(row.eligible_status?.status === "eligible" && isReadyStatus(row.status))}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>
                                                <Box>{row.name}</Box>
                                                <Box sx={{ color: 'grey', fontSize: '0.8rem' }}>{row.userid}</Box>
                                            </TableCell>
                                            <TableCell>{row.dept}</TableCell>
                                            <TableCell>
                                                <Box>{row.batch}</Box>
                                                <Box sx={{ color: 'grey', fontSize: '0.8rem' }}>{row.course}</Box>
                                            </TableCell>
                                            {activeTab === "ready" && (
                                                <TableCell>
                                                    {row.eligible_status?.status === "eligible" ? (
                                                        <img src={statusImg} alt="Status" style={{ width: '30px', height: '30px' }} />
                                                    ) : (
                                                        <Tooltip title="Profile is missing...">
                                                            <IconButton sx={{ color: 'error.main' }} onClick={handleErrorButton}>
                                                                <ErrorIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={filteredResults.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 20]}
                            sx={{ marginLeft: '30px', width: '100%', maxWidth: '1406px', backgroundColor: '#ffffff' }}
                        />
                        {activeTab === 'ready' && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginRight: '35px', marginBottom: '20px', marginTop: '10px' }}>
                                <Button variant="contained" color="primary" sx={{ fontFamily: 'sans-serif' }} onClick={handleGenerateClick}>
                                    Generate Certificate
                                </Button>
                            </Box>
                        )}

                        <Modal
                            open={openModal}
                            onClose={handleCloseModal}
                            aria-labelledby="how-sharing-works-modal"
                            aria-describedby="modal-to-explain-certificate-generation"
                        >
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 300,
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 3,
                                borderRadius: 2,
                                width: { xs: '90%', sm: '70%', md: '35%' },
                                height: '25%',
                                minHeight: '276px',
                                minWidth: '300px'
                            }}>
                                <Box sx={{
                                    background: '#1976d2',
                                    color: 'white',
                                    padding: '10px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                    <Typography id="how-sharing-works-modal" variant="h6" component="h2" sx={{ fontSize: '1.2rem', marginLeft: '1rem' }}>
                                        HOW SHARING WORKS
                                    </Typography>
                                    <Button onClick={handleCloseModal} sx={{ color: '#ffffff', minWidth: 'auto', padding: 0, marginRight: '1rem', }}>
                                        <Box sx={{ fontSize: '2rem', lineHeight: 1 }}>×</Box>
                                    </Button>
                                </Box>
                                <Typography id="modal-to-explain-certificate-generation" sx={{ mt: 2, color: 'black', lineHeight: '2', fontSize: '1.2rem' }}>
                                    Certificate will be generated.
                                </Typography>
                                <Typography sx={{ color: 'grey', lineHeight: '2', fontSize: '1.2rem' }}>
                                    Please note that you will need to share each
                                </Typography>
                                <Typography sx={{ color: 'grey', lineHeight: '1.5', fontSize: '1.2rem', mb: 2 }}>
                                    generated certificate individually.
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5 }}>
                                    <Button onClick={handleCloseModal} sx={{ mr: 2, fontFamily: 'sans-serif', color: '#ff9800', border: '1px solid #ff9800' }}>CANCEL</Button>
                                    <Button variant="contained" color="primary" sx={{ fontFamily: 'sans-serif' }} onClick={handleClick}>PROCEED</Button>
                                </Box>
                            </Box>
                        </Modal>

                        {/* Preview & Edit certificate data, one student at a time, before generating */}
                        <Modal
                            open={previewModalOpen}
                            onClose={handleCancelPreview}
                            aria-labelledby="preview-certificate-modal"
                            aria-describedby="modal-to-preview-and-edit-certificate-data"
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    bgcolor: 'background.paper',
                                    boxShadow: 24,
                                    p: 2,
                                    borderRadius: 2,
                                    width: '70%',
                                    '@media (max-width: 768px)': {
                                        width: '95%',
                                    },
                                    maxHeight: '90vh',
                                    overflowY: 'auto',
                                    maxWidth: '95vw',
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography id="preview-certificate-modal" variant="h6" component="h2">
                                        Preview Certificate Data ({previewIndex + 1} of {previewQueue.length}) — {previewQueue[previewIndex]}
                                    </Typography>
                                    <IconButton onClick={handleCancelPreview}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>

                                {previewLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                        <CircularProgress size={36} />
                                    </Box>
                                ) : (
                                    <>
                                        <Typography variant="h6" sx={{ mb: 1, color: '#673ab7' }}>Profile Photo</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                            <Box
                                                component="img"
                                                src={previewImagePreviewUrl || (previewCurrentImage ? `${process.env.REACT_APP_URL}/documents/${previewCurrentImage}` : certificateImage)}
                                                alt="Profile"
                                                sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1, border: '1px solid #E0E0E0' }}
                                            />
                                            <Box>
                                                <input
                                                    accept=".jpg,.jpeg,.png"
                                                    style={{ display: 'none' }}
                                                    id="preview-photo-upload"
                                                    type="file"
                                                    onChange={handlePreviewImageSelect}
                                                    disabled={!previewEditMode || previewImageUploading}
                                                />
                                                <label htmlFor="preview-photo-upload" style={{ pointerEvents: (!previewEditMode || previewImageUploading) ? 'none' : 'auto' }}>
                                                    <Button
                                                        variant="outlined"
                                                        component="span"
                                                        startIcon={previewImageUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                                        disabled={!previewEditMode || previewImageUploading}
                                                    >
                                                        {previewImageUploading ? 'Uploading...' : 'Upload New Photo'}
                                                    </Button>
                                                </label>
                                                {previewStagedImagePath && (
                                                    <Typography sx={{ color: 'success.main', fontSize: '0.85rem', mt: 1 }}>
                                                        New photo staged — will apply once approved.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        <Typography variant="h6" sx={{ mb: 1, color: '#673ab7' }}>Certificate Details</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                            <TextField
                                                label="Student Name"
                                                size="small"
                                                value={previewForm.student_name}
                                                onChange={(e) => handlePreviewFieldChange('student_name', e.target.value)}
                                                sx={{ width: 260 }}
                                                InputLabelProps={{ shrink: true }}
                                                disabled={!previewEditMode}
                                            />
                                            <TextField
                                                label="Course Name"
                                                size="small"
                                                value={previewForm.course}
                                                onChange={(e) => handlePreviewFieldChange('course', e.target.value)}
                                                sx={{ width: 260 }}
                                                InputLabelProps={{ shrink: true }}
                                                disabled={!previewEditMode}
                                            />
                                            <TextField
                                                label="Issue Date"
                                                type="date"
                                                size="small"
                                                value={previewForm.date}
                                                onChange={(e) => handlePreviewFieldChange('date', e.target.value)}
                                                sx={{ width: 180 }}
                                                InputLabelProps={{ shrink: true }}
                                                disabled={!previewEditMode}
                                            />
                                            <TextField
                                                label="Start Date"
                                                type="date"
                                                size="small"
                                                value={previewForm.start_date}
                                                onChange={(e) => handlePreviewFieldChange('start_date', e.target.value)}
                                                sx={{ width: 180 }}
                                                InputLabelProps={{ shrink: true }}
                                                disabled={!previewEditMode}
                                            />
                                            <TextField
                                                label="End Date"
                                                type="date"
                                                size="small"
                                                value={previewForm.end_date}
                                                onChange={(e) => handlePreviewFieldChange('end_date', e.target.value)}
                                                sx={{ width: 180 }}
                                                InputLabelProps={{ shrink: true }}
                                                disabled={!previewEditMode}
                                            />
                                        </Box>

                                        {previewStudentMode === 'online' ? (
                                            <Typography sx={{ color: 'grey', mb: 2 }}>
                                                Online students are certificated without assessment marks — no marks section applies.
                                            </Typography>
                                        ) : (
                                            <>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                                    <Typography variant="h6" sx={{ color: '#673ab7' }}>Marks</Typography>
                                                    {!previewMarksLoading && previewAssessments.length > 0 && (() => {
                                                        const overallTotal = calculatePreviewOverallTotal();
                                                        const { grade, label } = getGradeForMark(overallTotal);
                                                        return (
                                                            <>
                                                                <Box sx={{ bgcolor: '#ede7f6', color: '#673ab7', borderRadius: '12px', px: 1.5, py: 0.5, fontWeight: 600, fontSize: '0.85rem' }}>
                                                                    Total Marks: {overallTotal}
                                                                </Box>
                                                                <Box sx={{ bgcolor: '#e0f7ed', color: '#00a36f', borderRadius: '12px', px: 1.5, py: 0.5, fontWeight: 600, fontSize: '0.85rem' }}>
                                                                    Grade: {grade} ({label})
                                                                </Box>
                                                            </>
                                                        );
                                                    })()}
                                                </Box>
                                                {previewMarksLoading && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                                        <CircularProgress size={30} />
                                                    </Box>
                                                )}
                                                {!previewMarksLoading && previewAssessments.length === 0 && (
                                                    <Typography sx={{ color: 'grey', mb: 2 }}>No assessment marks found for this student.</Typography>
                                                )}
                                                {!previewMarksLoading && previewAssessments.map(item => {
                                                    const fields = MARKS_COMPONENT_MAP[item.generic_name] || [];
                                                    if (fields.length === 0) return null;
                                                    return (
                                                        <Box key={item.assessment_id} sx={{ mb: 2, p: 2, border: '1px solid #E0E0E0', borderRadius: '6px' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Typography variant="subtitle1" sx={{ color: '#673ab7', fontWeight: 600 }}>
                                                                    {item.generic_name}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        bgcolor: '#e0f7ed',
                                                                        color: '#00a36f',
                                                                        borderRadius: '12px',
                                                                        px: 1.5,
                                                                        py: 0.25,
                                                                        fontWeight: 600,
                                                                        fontSize: '0.8rem',
                                                                    }}
                                                                >
                                                                    Total Marks: {calculatePreviewAssessmentTotal(item.assessment_id)}
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                                                                {fields.map(field => {
                                                                    const maxMark = getMarkMax(field, item.generic_name);
                                                                    const currentVal = previewEditedMarks[item.assessment_id]?.[field];
                                                                    return (
                                                                        <TextField
                                                                            key={field}
                                                                            label={`${toSentenceCase(field)} (Max: ${maxMark})`}
                                                                            type="number"
                                                                            size="small"
                                                                            value={currentVal !== undefined ? currentVal : ''}
                                                                            onChange={(e) => handlePreviewMarkChange(item.assessment_id, field, e.target.value)}
                                                                            inputProps={{ min: 0, max: maxMark, step: 1 }}
                                                                            sx={{ width: 200 }}
                                                                            InputLabelProps={{ shrink: true }}
                                                                            disabled={!previewEditMode}
                                                                        />
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </>
                                        )}

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
                                            <Button variant="outlined" onClick={handleCancelStudentEdit} disabled={previewSubmitting}>
                                                Cancel
                                            </Button>
                                            {!previewEditMode && (
                                                <Button
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={() => setPreviewEditMode(true)}
                                                    disabled={previewSubmitting}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                            {Object.keys(buildPreviewDiff().newData).length === 0 ? (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<ArrowForwardIcon />}
                                                    onClick={handleUseAsIs}
                                                    disabled={previewSubmitting}
                                                >
                                                    Next
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={previewSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                                    onClick={handleSubmitPreviewEditRequest}
                                                    disabled={previewSubmitting}
                                                >
                                                    {previewSubmitting ? 'Submitting...' : 'Request Edit & Next'}
                                                </Button>
                                            )}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Modal>

                    </>
                ) : (
                    <>
                        <img
                            src={certificateImage}
                            alt="Certificate Generation Illustration"
                            style={{ width: '350px', height: '350px', marginLeft: 'auto', marginRight: 'auto', marginTop: '10px', display: 'block' }}
                        />
                        <Typography
                            variant="h6"
                            mt={2}
                            sx={{
                                color: '#481E82',
                                textAlign: 'center',
                                fontSize: '28px',
                                lineHeight: '124%',
                                letterSpacing: '0.25px',
                                fontFamily: 'sans-serif',
                            }}
                        >
                            Select Batch to generate certificates
                        </Typography>
                    </>
                )}
            </Box>
        </Box >
    );
}

export default GenerateCertificatePage;